import React, { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { bff } from '@/lib/http/bff'
import { timeAgo } from '@/utils/date'
import { useSidecarMeta } from '@/hooks/useSidecarMeta'
import OutcomeTrackLineAudioPlayerWithVisualizer from '@/features/system/developer/components/workflow/outcomes/OutcomeTrackLineAudioPlayerWithVisualizer'

// Successor to legacy /bff/services/media/audio-library (tombstoned 2026-05-06)
// Uses entity-based /bff/media-v2/audio-library — hotstream-aware, scope-key canonical ID

interface AudioItem {
  scope_key: string
  status: string
  title: string
  artist: string
  album: string
  audio_url: string  // Browser-playable URL via /bff/media/* proxy
  gs_uri: string     // Raw gs:// URI for OutcomeTrackLineAudioPlayer (uses /bff/gcs/stream internally)
  codec: string
  created_at?: string | null
  updated_at?: string | null
}

interface AudioLibraryPickerProps {
  onLoaded?: () => void
}

/** Time window after ingest in which we assume analysis workers are still running. */
const ANALYZE_WINDOW_MS = 15 * 60 * 1000 // 15 minutes

/**
 * Heuristic: is analysis likely still in-flight?
 * True when the track was created within ANALYZE_WINDOW_MS.
 * Workers typically finish BPM in <10s and beats in <90s on worker-dense.
 */
function isAnalyzingHeuristic(createdAt?: string | null): boolean {
  if (!createdAt) return false
  return (Date.now() - new Date(createdAt).getTime()) < ANALYZE_WINDOW_MS
}

function fetchLibrary() {
  const qp = new URLSearchParams({ _ts: String(Date.now()), limit: '200' })
  return bff.get<{ items: AudioItem[]; total: number }>(
    `/media-v2/audio-library?${qp.toString()}`
  )
}

function itemLabel(r: AudioItem): string {
  const artist = (r.artist ?? '').trim()
  const title = (r.title ?? '').trim()
  if (artist && title) return `${artist} – ${title}`
  return title || artist || r.scope_key
}

/**
 * Badge showing beat count from the sidecar, if available.
 * Shows a pulsing `♩…` amber indicator while analysis is in-flight (recent track, no beats yet).
 */
function BeatBadge({ gsUri, createdAt }: { gsUri: string; createdAt?: string | null }) {
  const { data: sidecar } = useSidecarMeta(gsUri)
  const beats = sidecar?.analysis?.beats
  if (!beats || beats.length === 0) {
    if (isAnalyzingHeuristic(createdAt)) {
      return (
        <span
          className="animate-pulse text-[9px] text-amber-500 leading-none"
          title="Beat analysis in progress…"
        >
          ♩…
        </span>
      )
    }
    return null
  }
  const downbeats = beats.filter(b => b.type === 'downbeat').length
  return (
    <span
      className="text-[9px] font-mono text-violet-400 leading-none"
      title={`${beats.length} beats / ${downbeats} downbeats`}
    >
      ♩{beats.length}
    </span>
  )
}

/**
 * AudioLibraryPicker — two-column layout:
 *   Left:  scrollable track list, click to select.
 *   Right: selected track in tall waveform mode with beat-grid auto-rendered
 *          from sidecar analysis.beats.
 *
 * Post-ingest, BPM + beats are triggered automatically by the BFF onComplete
 * hook (media.youtube.download DAG). The UI shows an "analyzing…" state for
 * tracks created within the last 15 min that have no analysis data yet, and
 * polls the sidecar every 5s until the workers deliver results.
 */
export default function AudioLibraryPicker({ onLoaded: _onLoaded }: AudioLibraryPickerProps) {
  const [search, setSearch] = useState('')
  const [selectedKey, setSelectedKey] = useState<string | null>(null)

  const libQuery = useQuery({
    queryKey: ['audioLibrary-v2'],
    queryFn: fetchLibrary,
    refetchInterval: 30_000,
    staleTime: 10_000,
  })

  const items = libQuery.data?.items ?? []

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return items
    return items.filter((r) => {
      const text = [r.artist, r.title, r.album, r.scope_key].join(' ').toLowerCase()
      return text.includes(q)
    })
  }, [items, search])

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const at = a.created_at ? new Date(a.created_at).getTime() : 0
      const bt = b.created_at ? new Date(b.created_at).getTime() : 0
      return bt - at
    })
  }, [filtered])

  const selected = useMemo(
    () => sorted.find(r => r.scope_key === selectedKey) ?? sorted[0] ?? null,
    [sorted, selectedKey],
  )

  // Determine if the selected track is likely still being analyzed.
  const selectedIsAnalyzing = isAnalyzingHeuristic(selected?.created_at)

  // Aggressively poll the selected track's sidecar while analysis is in-flight.
  // staleTime:0 + refetchInterval:5s forces TanStack Query to re-check GCS every 5s.
  // The shared query key means BeatBadge observers auto-update from the same cache slot.
  const { data: selectedSidecar } = useSidecarMeta(
    selected?.gs_uri,
    selectedIsAnalyzing ? { refetchInterval: 5_000, staleTime: 0 } : undefined,
  )

  const hasSelectedBeats = (selectedSidecar?.analysis?.beats?.length ?? 0) > 0
  const hasSelectedBpm   = selectedSidecar?.analysis?.bpm?.value != null
  // Still analyzing = heuristic says recent AND at least one analysis slot is missing.
  const analysisInProgress = selectedIsAnalyzing && (!hasSelectedBeats || !hasSelectedBpm)

  return (
    <div className="rounded-md border border-neutral-800/60 p-3 space-y-2">
      {/* Header */}
      <div className="flex items-baseline justify-between gap-2">
        <div className="text-xs text-neutral-300">Audio Library</div>
        <div className="text-[10px] text-neutral-600 font-mono flex items-center gap-1 flex-wrap justify-end">
          <kbd className="px-1 py-0.5 rounded bg-neutral-900 border border-neutral-800">m</kbd>
          <span>IN/OUT</span>
          <span>·</span>
          <kbd className="px-1 py-0.5 rounded bg-neutral-900 border border-neutral-800">c</kbd>
          <span>clear</span>
          <span>·</span>
          <kbd className="px-1 py-0.5 rounded bg-neutral-900 border border-neutral-800">[</kbd>
          <kbd className="px-0.5 py-0.5 rounded bg-neutral-900 border border-neutral-800">]</kbd>
          <span>speed</span>
          <span>·</span>
          <kbd className="px-1 py-0.5 rounded bg-neutral-900 border border-neutral-800">0</kbd>
          <span>1×</span>
          <span>·</span>
          <span className="text-violet-400">beats</span>
        </div>
      </div>

      {/* Filter */}
      <input
        type="search"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Filter tracks…"
        className="w-full px-2 py-1.5 rounded bg-neutral-900 border border-neutral-700/50 text-xs text-neutral-200 placeholder-neutral-600 outline-none focus:border-purple-500/40 transition-colors"
      />

      {/* Loading / error states */}
      {libQuery.isLoading && (
        <p className="text-xs text-neutral-500 py-3 text-center">Loading library…</p>
      )}
      {libQuery.isError && (
        <p className="text-xs text-rose-500 py-3 text-center">Failed to load library</p>
      )}
      {libQuery.isSuccess && sorted.length === 0 && (
        <p className="text-xs text-neutral-600 py-3 text-center">
          {items.length === 0 ? 'No tracks yet — ingest a YouTube URL above.' : 'No matching tracks.'}
        </p>
      )}

      {/* Two-column body */}
      {sorted.length > 0 && (
        <div className="flex gap-3 min-h-0">

          {/* ── Left list ────────────────────────────────────────────── */}
          <div className="w-44 shrink-0 flex flex-col gap-0.5 overflow-y-auto max-h-[420px] pr-1 scrollbar-thin scrollbar-track-transparent scrollbar-thumb-neutral-700">
            {sorted.map((r) => {
              const isActive = (selected?.scope_key === r.scope_key)
              return (
                <button
                  key={r.scope_key}
                  type="button"
                  onClick={() => setSelectedKey(r.scope_key)}
                  className={
                    'w-full text-left px-2 py-1.5 rounded transition-colors cursor-default ' +
                    (isActive
                      ? 'bg-neutral-800 border border-neutral-700/60'
                      : 'bg-transparent border border-transparent hover:bg-neutral-900 hover:border-neutral-800')
                  }
                >
                  <div className="flex items-start justify-between gap-1">
                    <span className={`text-[11px] font-mono truncate leading-tight ${isActive ? 'text-neutral-100' : 'text-neutral-400'}`}>
                      {itemLabel(r)}
                    </span>
                    {r.gs_uri && <BeatBadge gsUri={r.gs_uri} createdAt={r.created_at} />}
                  </div>
                  <div className="text-[9px] text-neutral-600 mt-0.5 flex items-center gap-1.5">
                    {r.codec && <span className="uppercase">{r.codec}</span>}
                    {r.created_at && <span>{timeAgo(r.created_at)}</span>}
                  </div>
                </button>
              )
            })}
          </div>

          {/* ── Right detail ─────────────────────────────────────────── */}
          <div className="flex-1 min-w-0">
            {selected ? (
              selected.gs_uri ? (
                <div className="space-y-1">
                  {/* Track identity */}
                  <div className="text-[11px] text-neutral-400 truncate font-mono px-0.5 flex items-center gap-2">
                    <span className="truncate">
                      {itemLabel(selected)}
                      {selected.album && (
                        <span className="text-neutral-600 ml-2">— {selected.album}</span>
                      )}
                    </span>
                    {/* BPM badge — shows when sidecar.analysis.bpm is present */}
                    {hasSelectedBpm && (
                      <span
                        className="shrink-0 text-[9px] font-mono text-emerald-500 leading-none"
                        title={`BPM: ${selectedSidecar?.analysis?.bpm?.value}`}
                      >
                        {selectedSidecar?.analysis?.bpm?.value} BPM
                      </span>
                    )}
                    {/* Pulsing "analyzing…" status for newly ingested tracks */}
                    {analysisInProgress && (
                      <span className="shrink-0 animate-pulse text-[9px] text-amber-500 leading-none flex items-center gap-1">
                        <span className="inline-block w-1.5 h-1.5 rounded-full bg-amber-500" />
                        analyzing…
                      </span>
                    )}
                  </div>

                  {/* Tall player with all lenses: markers + speed + beat-grid */}
                  <OutcomeTrackLineAudioPlayerWithVisualizer
                    key={selected.scope_key}
                    gsUri={selected.gs_uri}
                    scopeKey={selected.scope_key}
                    enableMarkers
                    enableSpeed
                    enableBeatGrid
                    analysisInProgress={analysisInProgress}
                    tall
                    visualizer={{ mode: 'frequency-response' }}
                  />
                </div>
              ) : (
                <div className="flex items-center h-20 justify-center text-[11px] text-neutral-600">
                  {selected.status}
                </div>
              )
            ) : (
              <div className="flex items-center h-20 justify-center text-[11px] text-neutral-600">
                Select a track
              </div>
            )}
          </div>

        </div>
      )}
    </div>
  )
}

